import { useEffect, useRef, useState } from 'react'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

interface GoogleSignInProps {
  itemId?: string
  onSignIn?: (user: { id: string; email: string; name: string | null }) => void
}

export default function GoogleSignIn({ itemId, onSignIn }: GoogleSignInProps) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [signedIn, setSignedIn] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || signedIn) return

    // Load Google Identity Services script
    const existing = document.getElementById('gsi-script')
    if (!existing) {
      const script = document.createElement('script')
      script.id = 'gsi-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
      script.onload = initGsi
    } else {
      initGsi()
    }

    function initGsi() {
      if (!window.google?.accounts?.id) {
        // Script loaded but API not ready, retry
        setTimeout(initGsi, 100)
        return
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        ux_mode: 'popup', // Use popup as fallback — redirect requires server-side form POST handling
      })

      if (buttonRef.current) {
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          width: 280,
          text: 'continue_with',
        })
      }
    }
  }, [signedIn])

  async function handleCredentialResponse(response: { credential: string }) {
    setLoading(true)
    try {
      const res = await fetch('/api/consumer-auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          credential: response.credential,
          item_id: itemId,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        setSignedIn(true)
        onSignIn?.(data.user)
      }
    } catch (err) {
      console.error('[GoogleSignIn] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!GOOGLE_CLIENT_ID) return null
  if (signedIn) {
    return (
      <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>
        Signed in — you'll be notified of matches
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2 mt-4">
      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        Get notified when we find your item
      </p>
      {loading ? (
        <div className="w-5 h-5 border-2 border-[var(--border-default)] border-t-[var(--text-muted)] rounded-full animate-spin" />
      ) : (
        <div ref={buttonRef} />
      )}
    </div>
  )
}

// Type declarations for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            ux_mode?: 'popup' | 'redirect'
            login_uri?: string
          }) => void
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: string
              size?: string
              shape?: string
              width?: number
              text?: string
            }
          ) => void
          prompt: () => void
        }
      }
    }
  }
}
